import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { SecretRedactor } from '@gitlab-org/secret-redaction';
import { Logger, withPrefix } from '@gitlab-org/logging';
import {
  methods,
  RequestError,
  type AgentContext,
  type PermissionOption,
  type PromptRequest,
  type PromptResponse,
} from '@agentclientprotocol/sdk';
import type { Message, ToolCall } from '@gitlab-org/tui';
import {
  AgentEventType,
  UserActionType,
  type ApprovalScope,
  type ToolApprovalAction,
  type UserAction,
} from '../../backend/backend';
import { SlashCommandService } from '../../slash_commands';
import type { AcpSessionState } from './acp_session_service';
import { extractText } from './translation/content';
import { computeMessageDelta } from './translation/messages';
import { buildToolCallSessionUpdate } from './translation/tool_calls';
import { planEntriesFor } from './translation/plan';

const OPTION_APPROVE_ONCE = 'approve-once';
const OPTION_APPROVE_SESSION = 'approve-session';
const OPTION_REJECT = 'reject';

export interface AcpPromptRunner {
  /** Runs a full prompt turn (including any tool-approval continuations) for a session. */
  run(params: PromptRequest, client: AgentContext, state: AcpSessionState): Promise<PromptResponse>;
}

export const AcpPromptRunner = createInterfaceId<AcpPromptRunner>('AcpPromptRunner');

@Implements(AcpPromptRunner)
@Service({
  dependencies: [SlashCommandService, SecretRedactor, Logger],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultAcpPromptRunner implements AcpPromptRunner {
  #slashCommandService: SlashCommandService;

  #secretRedactor: SecretRedactor;

  #logger: Logger;

  constructor(
    slashCommandService: SlashCommandService,
    secretRedactor: SecretRedactor,
    logger: Logger,
  ) {
    this.#slashCommandService = slashCommandService;
    this.#secretRedactor = secretRedactor;
    this.#logger = withPrefix(logger, '[AcpPromptRunner]');
  }

  async run(
    params: PromptRequest,
    client: AgentContext,
    state: AcpSessionState,
  ): Promise<PromptResponse> {
    if (state.turnInFlight) {
      throw RequestError.invalidRequest(
        undefined,
        `A prompt is already in progress for session ${params.sessionId}`,
      );
    }

    // A session/cancel can arrive after session/new but before this run()
    // (e.g. while the prompt handler awaits #requireAuth). cancel() sets the
    // flag but cancelStream() is a no-op with no turn in flight, so honour the
    // cancellation here instead of silently clearing it and running the prompt.
    if (state.cancelledDuringTurn) {
      state.cancelledDuringTurn = false;
      return { stopReason: 'cancelled' };
    }

    state.turnInFlight = true;

    try {
      const typedText = extractText(params.prompt);
      // Slash commands (custom commands, skills) expand to the prompt the
      // backend should receive; redaction must run after expansion since a
      // command body can inject text containing secrets.
      const expanded = await this.#slashCommandService.toPrompt(typedText);
      const prompt = this.#secretRedactor.redactSecrets(expanded ?? typedText, 'user-input');

      let action: UserAction = { type: UserActionType.SendPrompt, prompt };

      // A "turn" can pause one or more times for tool approval before it
      // truly ends — each pass through this loop is one segment between
      // pauses. Each segment depends on the previous decision, so this is
      // intentionally sequential.
      for (;;) {
        // eslint-disable-next-line no-await-in-loop -- sequential by necessity, see comment above
        const { pendingApprovals, hadError } = await this.#runTurnSegment(
          state,
          action,
          params.sessionId,
          client,
        );

        if (state.cancelledDuringTurn) return { stopReason: 'cancelled' };

        if (pendingApprovals.length === 0) {
          return { stopReason: hadError ? 'refusal' : 'end_turn' };
        }

        // Parallel tool calls awaiting approval are resolved one at a time;
        // remaining approvals in the batch surface again on the next segment.
        // eslint-disable-next-line no-await-in-loop -- must resolve this approval before the next segment can run
        const decision = await requestPermission(client, params.sessionId, pendingApprovals[0]);
        if (decision === 'cancelled') return { stopReason: 'cancelled' };
        action = decision;
      }
    } finally {
      state.turnInFlight = false;
      // Clear on turn completion rather than at turn start so a cancel that
      // races just before the next run() is observed by the guard above.
      // Remaining edge: a cancel that arrives in the window between this reset
      // and the next run()'s guard check is still lost, but that cancel refers
      // to a turn that has already ended, so dropping it is correct.
      state.cancelledDuringTurn = false;
    }
  }

  async #runTurnSegment(
    state: AcpSessionState,
    action: UserAction,
    sessionId: string,
    client: AgentContext,
  ): Promise<{ pendingApprovals: ToolCall[]; hadError: boolean }> {
    const stream = state.cliSession.sendMessageStream(action);
    const pendingApprovals = new Map<string, ToolCall>();
    let hadError = false;

    for await (const element of stream) {
      if (element.type === AgentEventType.Retry) {
        continue; // eslint-disable-line no-continue -- no ACP-side surface for retry progress yet
      }

      if (element.type === 'message') {
        if (element.role === 'assistant') {
          await emitMessageDelta(client, sessionId, element, state.sentTextLength);
        }
        continue; // eslint-disable-line no-continue -- user messages are already known to the client
      }

      if (element.type === 'tool') {
        // todo_write never becomes a tool card, not even while its arguments
        // are still streaming in (input is a `{ tool: 'pending' }` placeholder
        // then); announcing it as a tool_call here would leave a dangling
        // card once later sightings switch to plan updates instead of
        // tool_call_update. planEntriesFor returns undefined for the
        // placeholder input; that sighting produces nothing at all.
        // A todo_write that itself errored is the exception: fall through to
        // the generic tool path so the failure surfaces as a tool card rather
        // than a silent, successful-looking plan.
        if (element.name === 'todo_write' && element.state.type !== 'error') {
          const planResult = planEntriesFor(element.input);
          if (planResult) {
            planResult.warnings.forEach((warning) => this.#logger.warn(warning));
            await client.notify(methods.client.session.update, {
              sessionId,
              update: { sessionUpdate: 'plan', entries: planResult.entries },
            });
          }
          continue; // eslint-disable-line no-continue -- todo_write renders as a plan, never a tool card
        }

        const update = buildToolCallSessionUpdate(element, state.announcedToolCallIds);
        await client.notify(methods.client.session.update, { sessionId, update });

        if (element.state.type === 'approval_request') {
          pendingApprovals.set(element.id, element);
        } else {
          pendingApprovals.delete(element.id);
        }
        continue; // eslint-disable-line no-continue
      }

      if (element.type === 'error') {
        // A cancelled turn surfaces a generic ErrorMessage from the CLI
        // Session's abort handling; that isn't a real failure, so don't
        // forward it to the client or flip the turn into a refusal.
        if (state.cancelledDuringTurn) continue; // eslint-disable-line no-continue

        hadError = true;

        await emitErrorAsMessage(client, sessionId, element.error);
      }
    }

    return { pendingApprovals: [...pendingApprovals.values()], hadError };
  }
}

async function emitMessageDelta(
  client: AgentContext,
  sessionId: string,
  message: Message,
  sentTextLength: Map<string, number>,
): Promise<void> {
  const delta = computeMessageDelta(message, sentTextLength);
  if (!delta) return;

  await client.notify(methods.client.session.update, {
    sessionId,
    update: {
      sessionUpdate: 'agent_message_chunk',
      messageId: message.id,
      content: { type: 'text', text: delta },
    },
  });
}

async function emitErrorAsMessage(
  client: AgentContext,
  sessionId: string,
  errorText: string,
): Promise<void> {
  await client.notify(methods.client.session.update, {
    sessionId,
    update: {
      sessionUpdate: 'agent_message_chunk',
      content: { type: 'text', text: `Error: ${errorText}` },
    },
  });
}

/**
 * Requests a decision on a pending tool approval from the ACP client via
 * `session/request_permission`, offering allow-once, allow-for-session (only
 * when the tool call's `availableScopes` includes `'session'`), and reject. A
 * `cancelled` outcome (the client's answer when the turn is being cancelled)
 * ends the turn without sending any continuation to the backend.
 */
async function requestPermission(
  client: AgentContext,
  sessionId: string,
  toolCall: ToolCall,
): Promise<ToolApprovalAction | 'cancelled'> {
  const availableScopes: ApprovalScope[] =
    toolCall.state.type === 'approval_request' ? toolCall.state.availableScopes : ['once'];

  const options: PermissionOption[] = [
    { optionId: OPTION_APPROVE_ONCE, name: 'Allow once', kind: 'allow_once' },
  ];
  if (availableScopes.includes('session')) {
    options.push({
      optionId: OPTION_APPROVE_SESSION,
      name: 'Allow for this session',
      kind: 'allow_always',
    });
  }
  options.push({ optionId: OPTION_REJECT, name: 'Reject', kind: 'reject_once' });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured only to exclude `tool` from toolArgs
  const { tool: _tool, ...toolArgs } = toolCall.input as unknown as Record<string, unknown>;

  const response = await client.request(methods.client.session.requestPermission, {
    sessionId,
    toolCall: { toolCallId: toolCall.id },
    options,
  });

  const { outcome } = response;
  if (outcome.outcome === 'cancelled') {
    return 'cancelled';
  }
  if (outcome.optionId === OPTION_REJECT) {
    return {
      type: UserActionType.SendToolApproval,
      toolId: toolCall.id,
      toolName: toolCall.name,
      approved: false,
    };
  }

  const scope: ApprovalScope = outcome.optionId === OPTION_APPROVE_SESSION ? 'session' : 'once';
  return {
    type: UserActionType.SendToolApproval,
    toolId: toolCall.id,
    toolName: toolCall.name,
    toolArgs,
    approved: true,
    scope,
  };
}
